import { computed, inject, Service, signal } from '@angular/core';
import { IDienstleistungInfo, IDienstleistungSucheParams } from '@mng/mgs-lib';
import { firstValueFrom } from 'rxjs';
import { MgsWebApi } from '../../mgs-webapi.service';
import { DienstleistungService } from '../dienstleistung.service';
import { executeActionOnLogout } from '../../mgs-action-on-logout';
import { AuthStateFacade } from '../../auth/auth-state.facade';

export interface IDienstleistungListState {
  dienstleistungen: IDienstleistungInfo[];
  params: IDienstleistungSucheParams;
}

const defaultState: IDienstleistungListState = { dienstleistungen: [], params: {} };

@Service()
export class DienstleistungListService {
  readonly webApi = inject(MgsWebApi);

  #state = signal<IDienstleistungListState>(defaultState);
  dienstleistungen = computed(() => this.#state().dienstleistungen);
  params = computed(() => this.#state().params);

  readonly dienstleistungService = inject(DienstleistungService);

  // wenn sich die "updateAction" ändert dann wird das object neu "aufgebaut" -> deshalb ist im "computed" object ein signal
  // (das "setzbar" ist) definiert (das wird bei dieser Änderung immer auf false gesetzt)
  dienstleistungUpdatedState = computed(() => ({
    updateAction: this.dienstleistungService.dienstleistungUpdateAction(),
    loaded: signal(false)
  }));

  readonly authStateFacade = inject(AuthStateFacade);

  constructor() {
    executeActionOnLogout(() => this.#state.set(defaultState));
  }

  async findDienstleistungen(params: IDienstleistungSucheParams) {
    const result = (await firstValueFrom(this.webApi.dienstleistungenSuche(params))).data.result;
    this.#state.set({ dienstleistungen: result, params });
    this.dienstleistungUpdatedState().loaded.set(true);
  }

  async dienstleistungsBearbeitungStatusWechsel(id: number, statusId: number) {
    await firstValueFrom(this.webApi.dienstleistungsBearbeitungStatusWechsel({ id, statusId }));
    await this.updateNachDienstleistungAktion(id);
  }

  async dienstleistungLoeschen(id: number) {
    await firstValueFrom(this.webApi.dienstleistungFuerAdminLoeschen(id));
    await this.updateNachDienstleistungAktion(id);
  }

  private async updateNachDienstleistungAktion(id: number) {
    // Dienstleistungen neu lesen
    await this.findDienstleistungen(this.#state().params);
    this.dienstleistungService.resetWennAktiv(id);
  }
}
